import { useState, useRef, useEffect } from "react";
import { Sparkles, X, ArrowUp } from "lucide-react";
import { api } from "../lib/api";

interface Msg {
  role: "user" | "assistant";
  text: string;
}

export function AskGspDock({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [messages, setMessages] = useState<Msg[]>([
    { role: "assistant", text: "Hi, I'm Ask GSP — grounded in documented GSP process facts. Try: \"What happens if I reject a proposal?\"" },
  ]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const bodyRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    bodyRef.current?.scrollTo({ top: bodyRef.current.scrollHeight });
  }, [messages]);

  async function send(e: React.FormEvent) {
    e.preventDefault();
    if (!input.trim() || sending) return;
    const question = input.trim();
    setMessages((m) => [...m, { role: "user", text: question }]);
    setInput("");
    setSending(true);
    try {
      const { answer } = await api.post<{ answer: string }>("/ask-gsp", { question });
      setMessages((m) => [...m, { role: "assistant", text: answer }]);
    } catch {
      setMessages((m) => [...m, { role: "assistant", text: "Sorry, I couldn't reach the assistant service just now." }]);
    } finally {
      setSending(false);
    }
  }

  if (!open) return null;
  return (
    <div className="fixed bottom-6 right-6 z-50 w-[380px] max-w-[92vw] rounded-2xl shadow-2xl border border-slate-200 dark:border-white/10 bg-white dark:bg-surface-2 overflow-hidden">
      <div className="flex items-center gap-2 px-4 py-3 bg-sbd-black text-white">
        <Sparkles className="w-4 h-4 text-sbd-yellow" />
        <div className="text-sm font-semibold">Ask GSP</div>
        <span className="text-[10px] bg-white/10 rounded-full px-2 py-0.5 ml-1">Docs-grounded</span>
        <button className="ml-auto" onClick={onClose}>
          <X className="w-4 h-4" />
        </button>
      </div>
      <div ref={bodyRef} className="h-80 overflow-y-auto px-4 py-3 space-y-3 text-sm">
        {messages.map((m, i) => (
          <div key={i} className={m.role === "user" ? "text-right" : "text-left"}>
            <span
              className={`inline-block px-3 py-2 max-w-[85%] text-left ${
                m.role === "user" ? "bg-sbd-black text-white dark:bg-sbd-yellow dark:text-sbd-black rounded-2xl rounded-br-sm" : "bg-slate-100 dark:bg-white/10 rounded-2xl rounded-bl-sm"
              }`}
            >
              {m.text}
            </span>
          </div>
        ))}
      </div>
      <div className="border-t border-slate-200 dark:border-white/10 p-3">
        <form onSubmit={send} className="flex items-center gap-2">
          <input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="e.g. What happens if I reject a proposal?"
            className="flex-1 text-sm rounded-lg border border-slate-200 dark:border-white/10 bg-transparent px-3 py-2 outline-none focus:ring-2 focus:ring-sbd-yellow/50"
          />
          <button disabled={sending} className="h-9 w-9 rounded-lg bg-sbd-black text-white dark:bg-sbd-yellow dark:text-sbd-black flex items-center justify-center disabled:opacity-50">
            <ArrowUp className="w-4 h-4" />
          </button>
        </form>
      </div>
    </div>
  );
}
